import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Stack, Chip, IconButton,
  InputAdornment, MenuItem, Select, FormControl, InputLabel,
  Divider, Grid, Card
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import axios from 'axios';
import API_BASE_URL from '../config';


const AdminInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);

  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [selectedInvoiceData, setSelectedInvoiceData] = useState(null);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/admin/invoices`, { withCredentials: true });
      setInvoices(res.data || []);
      setError(null);
    } catch (err) {
      console.error("Fetch invoices error:", err);
      setError("Failed to fetch invoices. Please check your connection and admin privileges.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const filteredInvoices = invoices.filter((inv) => {
    const fullName = `${inv.first_name || ''} ${inv.last_name || ''}`.toLowerCase();
    const search = searchTerm.toLowerCase();
    const matchesSearch = 
      fullName.includes(search) ||
      inv.payment_id?.toLowerCase().includes(search) ||
      inv.order_id?.toLowerCase().includes(search) ||
      inv.uid?.toLowerCase().includes(search);

    const matchesStatus = statusFilter === 'All' || 
      (statusFilter === 'Paid' && (inv.payment_status === 'success' || inv.payment_status === 'paid')) ||
      (statusFilter === 'Failed' && inv.payment_status === 'failed');

    return matchesSearch && matchesStatus;
  });

  const handleView = (invoice) => {
    setSelectedInvoice(invoice);
    setViewOpen(true);
  };

  const handleInvoiceClick = (invoice) => {
    setSelectedInvoiceData(invoice);
    setInvoiceOpen(true);
  };

  const getStatusChip = (status) => {
    const isSuccess = status === 'success' || status === 'paid';
    return (
      <Chip 
        label={isSuccess ? 'Paid' : 'Failed'} 
        color={isSuccess ? 'success' : 'error'} 
        size="small" 
        sx={{ fontWeight: 'bold' }}
      />
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <ReceiptLongIcon color="primary" sx={{ fontSize: 40 }} />
        <Typography variant="h4" fontWeight="800" color="text.primary">Admin Invoices</Typography>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by User, UID, Order or Payment ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (<InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>),
                sx: { borderRadius: 2 }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={statusFilter}
                label="Payment Status"
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="All">All Status</MenuItem>
                <MenuItem value="Paid">Paid</MenuItem>
                <MenuItem value="Failed">Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button fullWidth variant="contained" onClick={fetchInvoices} sx={{ borderRadius: 2, height: '40px' }}>Refresh</Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 10 }}><CircularProgress /><Typography sx={{ mt: 2 }} color="text.secondary">Loading records...</Typography></Box>
      ) : error ? (
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      ) : filteredInvoices.length === 0 ? (
        <Paper sx={{ textAlign: 'center', py: 10, borderRadius: 3 }}><Typography color="text.secondary">No invoices found.</Typography></Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 3 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>User</TableCell>
                <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>Transaction IDs</TableCell>
                <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>Amount</TableCell>
                <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInvoices.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{`${row.first_name} ${row.last_name}`}</Typography>
                    <Typography variant="caption" color="text.secondary">UID: {row.uid}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" display="block">Order: {row.order_id}</Typography>
                    <Typography variant="caption" color="primary">Pay: {row.payment_id}</Typography>
                  </TableCell>
                  <TableCell><Typography variant="body1" fontWeight="800">₹{row.amount}</Typography></TableCell>
                  <TableCell>{getStatusChip(row.payment_status)}</TableCell>
                  <TableCell><Typography variant="body2">{new Date(row.created_at).toLocaleDateString()}</Typography></TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <IconButton color="info" onClick={() => handleView(row)} size="small"><VisibilityIcon /></IconButton>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => handleInvoiceClick(row)}
                        sx={{ textTransform: 'none', borderRadius: 1.5, fontSize: 12, fontWeight: 'bold' }}
                      >
                        Invoice
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
          <Stack alignItems="center">
            <Box sx={{ p: 2, bgcolor: alpha('#1976d2', 0.1), borderRadius: '50%', mb: 1 }}><ReceiptLongIcon color="primary" fontSize="large" /></Box>
            <Typography variant="h5" fontWeight="bold">Invoice Summary</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <Box sx={{ mt: 1 }}>
              <Card variant="outlined" sx={{ p: 2, bgcolor: '#fbfbfb', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">{selectedInvoice.first_name} {selectedInvoice.last_name}</Typography>
                <Typography variant="body2" color="text.secondary">UID: {selectedInvoice.uid}</Typography>
              </Card>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                   <Typography variant="caption" color="text.secondary">Transaction Date</Typography>
                   <Typography variant="body2" fontWeight="bold">{new Date(selectedInvoice.created_at).toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={12}><Divider /></Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Total Paid</Typography>
                  <Typography variant="h5" fontWeight="900" color="primary">₹{selectedInvoice.amount}</Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box>{getStatusChip(selectedInvoice.payment_status)}</Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button fullWidth variant="outlined" onClick={() => setViewOpen(false)} color="inherit">Close</Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default AdminInvoices;